import axios from 'axios';
import {
  BulkRetryExecuteRequest,
  BulkRetryExecuteResponse,
  BulkRetryFilter,
  BulkRetryPreviewResponse,
} from '../interfaces/bulkReprocess';
import {
  ChangeDetectedDocumentTypeRequest,
  ChangeDetectedDocumentTypeResponse,
  DiscardDetectedDocumentRequest,
  DiscardDetectedDocumentResponse,
  DiscardPatientDocumentRequest,
  DiscardPatientDocumentResponse,
  DocumentDetailResponse,
  DocumentListParams,
  DocumentListResponse,
  DocumentType,
  DocumentTypeResponse,
  LlmPromptListResponse,
  LlmPromptResponse,
} from '../interfaces/document';
import {
  NotEngagedReferrals,
  ReferralNotEngagedReferralsResponse,
  ReferralResultResponse,
  DiscardReferralResponse,
  OpsReferral,
  OpsReferralAuditHistory,
  OpsReferralListParams,
  OpsReferralListResponse,
  UpdateReferralRequest,
  UpdateReferralResponse,
  UploadUrlResult,
} from '../interfaces/referral';
import { normalizeReferralFileName } from '../util/stringFormat';
import { AuthService } from './';

const LEADS_BASE_URL = process.env.REACT_APP_PUBLIC_LEADS_API_BASE_URL;

const DEBUG_PHONE_PARAM = 'debugPhone';

/**
 * The only way this service logs a failed request.
 *
 * `console.error(..., error)` on an axios error serialises the whole request config. On this
 * service that means, depending on the call: the presigned S3 upload URL and its credentials,
 * a URL carrying `searchPatientName`, the demographics PATCH body (name, date of birth,
 * address, email, phone) — and on every one of them the `Authorization: Bearer` header.
 *
 * That is PHI and credentials in the browser console, and in anything that ships console
 * output onward. Status and message are what a reader needs anyway.
 */
const logRequestFailure = (context: string, error: unknown): void => {
  const axiosLike = error as { response?: { status?: number }; message?: string };

  console.error(
    `${context} failed${axiosLike?.response?.status ? ` (${axiosLike.response.status})` : ''}: ${
      axiosLike?.message ?? 'unknown error'
    }`,
  );
};

/**
 * A 400 from the referral edit, as a failed update carrying the API's own message, so the card
 * says what to fix instead of "Failed to update referral". leads-api 0.64.0 refuses a BMI
 * outside 10–100 this way ("BMI must be between 10 and 100"); its validation messages name
 * the field and the rule, never the value sent. The message is shown, not logged:
 * `logRequestFailure` keeps to the status. `null` for anything other than a 400 with a message.
 */
const validationRefusal = (referralId: number, error: unknown): UpdateReferralResponse | null => {
  const response = (error as { response?: { status?: number; data?: { message?: unknown } } })
    ?.response;
  if (response?.status !== 400) {
    return null;
  }
  const raw = response.data?.message;
  const messages = (Array.isArray(raw) ? raw : [raw]).filter(
    (message): message is string => typeof message === 'string' && message.trim() !== '',
  );
  if (messages.length === 0) {
    return null;
  }
  return { success: false, referralId, updatedFields: [], message: messages.join('; ') };
};

class ReferralService {
  public getDebugPhoneFromUrl(urlSearch: string): string | null {
    return new URLSearchParams(urlSearch).get(DEBUG_PHONE_PARAM);
  }

  /**
   * @param fileType MIME type to declare. The API stores the object under an extension derived
   *   from it, and ingest routes on that extension — so declaring `application/pdf` for a
   *   referral package would send XML into the PDF pipeline.
   */
  public async createUploadUrl(
    fileName: string,
    fileType = 'application/pdf',
  ): Promise<UploadUrlResult | null> {
    const authData = AuthService.getAuth();

    try {
      const { uploadUrl, destination } = (
        await axios.post<UploadUrlResult>(
          `${LEADS_BASE_URL}/v1/referrals/create-upload-link`,
          { fileName: normalizeReferralFileName(fileName), fileType },
          { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
        )
      ).data;

      return { uploadUrl, destination };
    } catch (error) {
      logRequestFailure('Referral request', error);
    }

    return null;
  }

  /**
   * @param contentType must be the type the upload URL was signed for. The presigned PUT is
   *   bound to it, and axios otherwise infers the header from the `File`, which the browser
   *   reports inconsistently — Windows calls a ZIP `application/x-zip-compressed` and some
   *   browsers report nothing at all, either of which S3 rejects with a 403 that reads as a
   *   permissions problem rather than a mismatched header.
   */
  public async pushContentAndParseReferral(
    clinicCode: string,
    uploadUrl: string,
    sourceFile: string,
    referral: File,
    contentType = 'application/pdf',
  ): Promise<ReferralResultResponse | null> {
    const authData = AuthService.getAuth();

    try {
      // Upload referral file to S3
      await axios.put(uploadUrl, referral, {
        timeout: 60 * 1000,
        headers: { 'Content-Type': contentType },
      });

      const url: string = `${LEADS_BASE_URL}/v1/clinic/${clinicCode}/fax/${sourceFile}/parse`;

      // Parse uploaded referral fax
      const parseResponse = (
        await axios.get<ReferralResultResponse>(url, {
          timeout: 60 * 1000,
          headers: { Authorization: `Bearer ${authData?.auth_token}` },
        })
      ).data;

      return parseResponse;
    } catch (error) {
      logRequestFailure('Referral request', error);
    }

    return null;
  }

  public async sendSmsForReferral(
    parsingResult: ReferralResultResponse,
    debugPhone: string | null,
  ): Promise<{ status: boolean; reason: string | null }> {
    const {
      referral: { clinicReferralId },
    } = parsingResult;

    if (!clinicReferralId) {
      return { status: false, reason: 'Missing referral ID' };
    }

    const authData = AuthService.getAuth();

    try {
      if (!debugPhone) {
        const referral = await this.fetchReferral(clinicReferralId);

        if (referral) {
          const {
            referral: {
              member: { smsNotifications },
            },
          } = referral;

          if (smsNotifications?.find(({ type }) => type === 'partner-referral')) {
            return {
              status: false,
              reason: `The sms was already sent for referral ID  - ${clinicReferralId}`,
            };
          }
        }
      }

      const { smsContent } = (
        await axios.post<{ smsContent: string }>(
          `${LEADS_BASE_URL}/v1/referrals/${clinicReferralId}/send`,
          { debugPhone },
          { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
        )
      ).data;

      return { status: true, reason: smsContent };
    } catch (error) {
      logRequestFailure('Referral request', error);

      return { status: false, reason: (error as any).message };
    }
  }

  public async fetchNotEngagedReferrals(): Promise<NotEngagedReferrals | null> {
    const authData = AuthService.getAuth();

    try {
      const { referrals } = (
        await axios.get<ReferralNotEngagedReferralsResponse>(
          `${LEADS_BASE_URL}/v1/referrals/not-engaged`,
          { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
        )
      ).data;

      return referrals
        .sort((r1, r2) => (Date.parse(r2.referralDate) || -1) - (Date.parse(r1.referralDate) || -1))
        .reduce((notEngaged, r) => {
          const { clinicReferralId, ...rest } = r;
          return { ...notEngaged, [r.clinicReferralId]: { ...rest, id: clinicReferralId } };
        }, {} as NotEngagedReferrals);
    } catch (error) {
      logRequestFailure('Referral request', error);

      return null;
    }
  }

  public async fetchReferral(clinicReferralId: string): Promise<ReferralResultResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const referral = (
        await axios.get<ReferralResultResponse>(
          `${LEADS_BASE_URL}/v1/referrals/${clinicReferralId}`,
          { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
        )
      ).data;

      return referral;
    } catch (error) {
      logRequestFailure('Referral request', error);

      return null;
    }
  }

  public async fetchDocuments(params: DocumentListParams): Promise<DocumentListResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', String(params.page || 1));
      queryParams.append('limit', String(params.limit || 20));

      // Search parameters
      if (params.searchDocumentId) {
        queryParams.append('searchDocumentId', params.searchDocumentId);
      }
      if (params.searchCrmId) {
        queryParams.append('searchCrmId', params.searchCrmId);
      }
      if (params.searchPatientName) {
        queryParams.append('searchPatientName', params.searchPatientName);
      }

      // Sorting
      if (params.sortBy) {
        queryParams.append('sortBy', params.sortBy);
      }
      if (params.sortOrder) {
        queryParams.append('sortOrder', params.sortOrder);
      }

      // Filter arrays
      if (params.documentTypes?.length) {
        params.documentTypes.forEach((type) => queryParams.append('documentTypes', type));
      }
      if (params.processingStatuses?.length) {
        params.processingStatuses.forEach((status) =>
          queryParams.append('processingStatuses', status),
        );
      }
      if (params.reviewStates?.length) {
        params.reviewStates.forEach((state) => queryParams.append('reviewStates', state));
      }
      if (params.processingSteps?.length) {
        params.processingSteps.forEach((step) => queryParams.append('processingSteps', step));
      }
      if (params.sourceTypes?.length) {
        params.sourceTypes.forEach((source) => queryParams.append('sourceTypes', source));
      }
      if (params.receivingClinicIds?.length) {
        params.receivingClinicIds.forEach((id) =>
          queryParams.append('receivingClinicIds', String(id)),
        );
      }
      if (params.detectedClinicIds?.length) {
        params.detectedClinicIds.forEach((id) =>
          queryParams.append('detectedClinicIds', String(id)),
        );
      }
      if (params.crmIds?.length) {
        params.crmIds.forEach((id) => queryParams.append('crmIds', id));
      }

      // Boolean filters
      if (params.hasReferrals !== undefined) {
        queryParams.append('hasReferrals', String(params.hasReferrals));
      }
      if (params.hasLinkedUsers !== undefined) {
        queryParams.append('hasLinkedUsers', String(params.hasLinkedUsers));
      }
      if (params.requiresReview !== undefined) {
        queryParams.append('requiresReview', String(params.requiresReview));
      }
      if (params.isProcessed !== undefined) {
        queryParams.append('isProcessed', String(params.isProcessed));
      }

      // Date filters
      if (params.dateFrom) {
        queryParams.append('dateFrom', params.dateFrom);
      }
      if (params.dateTo) {
        queryParams.append('dateTo', params.dateTo);
      }

      const response = await axios.get<DocumentListResponse>(
        `${LEADS_BASE_URL}/v1/documents?${queryParams.toString()}`,
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );

      return response.data;
    } catch (error) {
      logRequestFailure('Fetching documents', error);
      return null;
    }
  }

  /**
   * The inbound document holding the referral for a CRM lead (LeadSquared id), for linking an
   * eligibility task back to its referral. leads-api scopes the list to the caller's clinics,
   * so a referral the caller may not see comes back as no match. Any failure (403 included) is
   * `null`: the caller hides the link rather than explaining why.
   */
  public async findReferralDocumentByCrmId(
    crmId: string,
  ): Promise<{ documentId: number; referralId?: number } | null> {
    try {
      const response = await this.fetchDocuments({
        crmIds: [crmId],
        hasReferrals: true,
        limit: 1,
      });
      // Exact match only, as the eligibility direction does: a leads-api that ignored the
      // filter would otherwise link an unrelated lead's referral.
      const match = response?.documents?.find((document) => document.crmId === crmId);

      return match ? { documentId: match.documentId, referralId: match.referralId } : null;
    } catch {
      // `fetchDocuments` reads the stored auth before its own try; a corrupt entry lands here.
      console.error('Referral lookup by crmId failed');
      return null;
    }
  }

  public async getDocumentDetail(documentId: number): Promise<DocumentDetailResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const document = (
        await axios.get<DocumentDetailResponse>(`${LEADS_BASE_URL}/v1/documents/${documentId}`, {
          headers: { Authorization: `Bearer ${authData?.auth_token}` },
        })
      ).data;

      return document;
    } catch (error) {
      logRequestFailure('Referral request', error);
      return null;
    }
  }

  public async updateDetectedDocument(
    detectedDocId: number,
    data: Record<string, any>,
  ): Promise<boolean> {
    const authData = AuthService.getAuth();

    try {
      await axios.put(`${LEADS_BASE_URL}/v1/detected-documents/${detectedDocId}`, data, {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
      });
      return true;
    } catch (error) {
      logRequestFailure('Referral request', error);
      return false;
    }
  }

  public async retryDocumentProcessing(
    documentId: number,
    reason?: string,
  ): Promise<{ success: boolean; message: string }> {
    const authData = AuthService.getAuth();

    try {
      const response = await axios.post(
        `${LEADS_BASE_URL}/v1/documents/${documentId}/retry`,
        { reason },
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );
      return response.data;
    } catch (error) {
      logRequestFailure('Retrying document processing', error);
      throw error;
    }
  }

  public async bulkRetryPreview(filter: BulkRetryFilter): Promise<BulkRetryPreviewResponse> {
    const authData = AuthService.getAuth();

    const response = await axios.post<BulkRetryPreviewResponse>(
      `${LEADS_BASE_URL}/v1/admin/documents/bulk-retry/preview`,
      filter,
      { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
    );
    return response.data;
  }

  public async bulkRetryExecute(
    payload: BulkRetryExecuteRequest,
  ): Promise<BulkRetryExecuteResponse> {
    const authData = AuthService.getAuth();

    const response = await axios.post<BulkRetryExecuteResponse>(
      `${LEADS_BASE_URL}/v1/admin/documents/bulk-retry/execute`,
      payload,
      { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
    );
    return response.data;
  }

  public async markDocumentAsResolved(
    documentId: number,
    reason: string,
  ): Promise<{ success: boolean; message: string }> {
    const authData = AuthService.getAuth();

    try {
      const response = await axios.post(
        `${LEADS_BASE_URL}/v1/documents/${documentId}/resolve`,
        { reason },
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );
      return response.data;
    } catch (error) {
      logRequestFailure('Marking document as resolved', error);
      throw error;
    }
  }

  public async changeDetectedDocumentType(
    documentId: number,
    detectedDocumentId: number,
    body: ChangeDetectedDocumentTypeRequest,
  ): Promise<ChangeDetectedDocumentTypeResponse> {
    const authData = AuthService.getAuth();

    const response = await axios.patch<ChangeDetectedDocumentTypeResponse>(
      `${LEADS_BASE_URL}/v1/documents/${documentId}/detected-documents/${detectedDocumentId}`,
      body,
      { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
    );
    return response.data;
  }

  public async discardDetectedDocument(
    documentId: number,
    detectedDocumentId: number,
    body: DiscardDetectedDocumentRequest,
  ): Promise<DiscardDetectedDocumentResponse> {
    const authData = AuthService.getAuth();

    const response = await axios.delete<DiscardDetectedDocumentResponse>(
      `${LEADS_BASE_URL}/v1/documents/${documentId}/detected-documents/${detectedDocumentId}`,
      {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
        data: body,
      },
    );
    return response.data;
  }

  public async discardPatientDocument(
    documentId: number,
    detectedDocumentId: number,
    patientDocumentId: number,
    body: DiscardPatientDocumentRequest,
  ): Promise<DiscardPatientDocumentResponse> {
    const authData = AuthService.getAuth();

    const response = await axios.delete<DiscardPatientDocumentResponse>(
      `${LEADS_BASE_URL}/v1/documents/${documentId}/detected-documents/${detectedDocumentId}/patient-documents/${patientDocumentId}`,
      {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
        data: body,
      },
    );
    return response.data;
  }

  public async fetchDocumentTypes(): Promise<DocumentTypeResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await axios.get<DocumentTypeResponse>(
        `${LEADS_BASE_URL}/v1/admin/document-contents?limit=200`,
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );

      return response.data;
    } catch (error) {
      logRequestFailure('Fetching document types', error);
      return null;
    }
  }

  public async fetchDocumentType(id: number): Promise<DocumentType | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await axios.get<DocumentType>(
        `${LEADS_BASE_URL}/v1/admin/document-contents/${id}`,
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );

      return response.data;
    } catch (error) {
      logRequestFailure('Fetching document type', error);
      return null;
    }
  }

  public async fetchLlmPrompts(params?: {
    page?: number;
    limit?: number;
  }): Promise<LlmPromptListResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', String(params?.page || 1));
      queryParams.append('limit', String(params?.limit || 20));

      const response = await axios.get<LlmPromptListResponse>(
        `${LEADS_BASE_URL}/v1/admin/llm-prompts?${queryParams.toString()}`,
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );

      return response.data;
    } catch (error) {
      logRequestFailure('Fetching LLM prompts', error);
      return null;
    }
  }

  public async fetchLlmPrompt(id: number): Promise<LlmPromptResponse | null> {
    const authData = AuthService.getAuth();

    try {
      const response = await axios.get<LlmPromptResponse>(
        `${LEADS_BASE_URL}/v1/admin/llm-prompts/${id}`,
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );

      return response.data;
    } catch (error) {
      logRequestFailure('Fetching LLM prompt', error);
      return null;
    }
  }

  public async updateReferralDocument(
    referralId: number,
    updateData: UpdateReferralRequest,
  ): Promise<UpdateReferralResponse | null> {
    const authData = AuthService.getAuth();

    // No editor in the body. leads-api (from #123, in production since 0.62.6) records the
    // editor of a referral edit from the access token, the same way the discard endpoint does,
    // and ignores `changedByUserId`/`changedByUserFullName` if sent. A trail the client could
    // fill in is not an audit trail (HIPAA §164.312(b)).
    try {
      const response = await axios.patch<UpdateReferralResponse>(
        `${LEADS_BASE_URL}/v1/documents/referrals/${referralId}`,
        updateData,
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );

      return response.data;
    } catch (error) {
      logRequestFailure('Updating referral document', error);
      const refusal = validationRefusal(referralId, error);
      if (refusal) {
        return refusal;
      }
      return null;
    }
  }

  /**
   * The ops Referrals list. Rethrows, unlike the older methods here: an empty list and a
   * failed request must not look the same to someone working a queue.
   */
  public async listOpsReferrals(params: OpsReferralListParams): Promise<OpsReferralListResponse> {
    const authData = AuthService.getAuth();
    const query = new URLSearchParams();

    if (params.page) query.append('page', String(params.page));
    if (params.limit) query.append('limit', String(params.limit));
    if (params.search?.trim()) query.append('search', params.search.trim());
    if (params.sortOrder) query.append('sortOrder', params.sortOrder);
    if (params.includeDiscarded) query.append('includeDiscarded', 'true');
    params.clinicIds?.forEach((id) => query.append('clinicIds', String(id)));
    params.reviewStates?.forEach((state) => query.append('reviewStates', state));
    params.systemSources?.forEach((source) => query.append('systemSources', source));

    try {
      const response = await axios.get<OpsReferralListResponse>(
        `${LEADS_BASE_URL}/v1/ops/referrals?${query.toString()}`,
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );
      return response.data;
    } catch (error) {
      // Status and message only: the URL carries the patient-name search.
      logRequestFailure('Listing referrals', error);
      throw error;
    }
  }

  public async getOpsReferral(referralId: number): Promise<OpsReferral> {
    const authData = AuthService.getAuth();

    try {
      const response = await axios.get<OpsReferral>(`${LEADS_BASE_URL}/v1/ops/referrals/${referralId}`, {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
      });
      return response.data;
    } catch (error) {
      logRequestFailure('Fetching referral', error);
      throw error;
    }
  }

  public async getOpsReferralAuditHistory(referralId: number): Promise<OpsReferralAuditHistory> {
    const authData = AuthService.getAuth();

    try {
      const response = await axios.get<OpsReferralAuditHistory>(
        `${LEADS_BASE_URL}/v1/ops/referrals/${referralId}/audit-history`,
        { headers: { Authorization: `Bearer ${authData?.auth_token}` } },
      );
      return response.data;
    } catch (error) {
      logRequestFailure('Fetching referral history', error);
      throw error;
    }
  }

  /** Soft-discards a referral. The reason is recorded and audited, so it is required. */
  public async discardReferral(
    referralId: number,
    reason: string,
  ): Promise<DiscardReferralResponse> {
    const authData = AuthService.getAuth();

    const response = await axios.delete<DiscardReferralResponse>(
      `${LEADS_BASE_URL}/v1/referrals/${referralId}`,
      {
        headers: { Authorization: `Bearer ${authData?.auth_token}` },
        data: { reason },
      },
    );

    return response.data;
  }
}

const referralService = new ReferralService();

export default referralService;
